import { ed25519 } from "@noble/curves/ed25519.js";
import { blake3 } from "@noble/hashes/blake3.js";

import type { ActionView } from "../../types/wire";
import { getCachedDeriveMlDsa } from "../ml_dsa";

import { bytesToHex, concatBytes, u64LE, vu64 } from "./bytes";
import {
  encMlDsaLegOption,
  encSigningCredOption,
  type MlDsaLegFields,
  type SigningCredFields,
} from "./signing_cred";

/**
 * `ActionEnvelope` wire-format encoder + canonical signing payload
 * computation. Byte-equal to `arkhe-holdem-actions::envelope` (Rust
 * postcard `enc_action_envelope` / `sign_payload_action`). Gated by
 * the `tests/cross_impl/envelope_8case.test.ts` fixture (8 canonical
 * cases generated by
 * `arkhe-holdem-actions/tests/cross_impl_envelope_fixture.rs`).
 *
 * Postcard wire layout (matches Rust serde derive on
 * `ActionEnvelope`):
 *
 *     table_id: [u8; 16]            // 16 fixed bytes
 *     hand_seq: u64                 // postcard varint
 *     action_seq: u64               // postcard varint
 *     player_id: [u8; 32]           // 32 fixed bytes
 *     action: PlayerAction          // 1-byte tag + optional varint
 *     sig: [u8; 64]                 // 64 fixed bytes
 *     signing_cred: Option<..>      // 0x00 None (trailing Option)
 *     ml_dsa_leg: Option<..>        // 0x00 None (trailing Option)
 *
 * The two trailing `0x00` bytes are the postcard `None` discriminants
 * for the `#[serde(default)]` `signing_cred` + `ml_dsa_leg`
 * fields. postcard is positional, so a non-hybrid genesis envelope must
 * still emit BOTH discriminants after `sig` to stay byte-equal with the
 * Rust `postcard::to_allocvec`. The FE does not yet POPULATE either leg
 * (rotation / hybrid signing UX is a later phase) — it only emits the
 * `None` discriminants so the wire shape matches.
 */

/** Domain separator bound into the BLAKE3 signing payload prefix
 *  (`sign_payload_action`). Matches Rust `envelope::DOMAIN_PREFIX`
 *  byte-for-byte. */
export const DOMAIN_ACTION = new TextEncoder().encode(
  "arkhe-holdem::player-action::v1",
);

const ACTION_TAG_FOLD = 0x00;
const ACTION_TAG_CHECK = 0x01;
const ACTION_TAG_CALL = 0x02;
const ACTION_TAG_BET = 0x03;
const ACTION_TAG_RAISE = 0x04;
const ACTION_TAG_ALLIN = 0x05;

/** `ChipStack` is `#[serde(transparent)]` over `u64`, so it
 *  serializes as a bare varint with no extra framing — same shape
 *  as Bet.amount / Raise.to. */
export function encAction(action: ActionView): Uint8Array {
  switch (action.kind) {
    case "fold":
      return new Uint8Array([ACTION_TAG_FOLD]);
    case "check":
      return new Uint8Array([ACTION_TAG_CHECK]);
    case "call":
      return new Uint8Array([ACTION_TAG_CALL]);
    case "all_in":
      return new Uint8Array([ACTION_TAG_ALLIN]);
    case "bet": {
      const amount = vu64(BigInt(action.amount));
      const out = new Uint8Array(1 + amount.length);
      out[0] = ACTION_TAG_BET;
      out.set(amount, 1);
      return out;
    }
    case "raise": {
      const to = vu64(BigInt(action.to));
      const out = new Uint8Array(1 + to.length);
      out[0] = ACTION_TAG_RAISE;
      out.set(to, 1);
      return out;
    }
  }
}

/**
 * Canonical 32-byte BLAKE3 signing payload for one player action.
 * Layout matches `arkhe_holdem_actions::envelope::sign_payload`:
 *
 *     BLAKE3(DOMAIN_ACTION || table_id || hand_seq_le || action_seq_le
 *            || postcard(action))
 *
 * Returns a 32-byte digest the caller passes to `ed25519.sign`.
 */
export function signPayloadAction(
  tableId: Uint8Array,
  handSeq: bigint,
  actionSeq: bigint,
  action: ActionView,
): Uint8Array {
  if (tableId.length !== 16) {
    throw new RangeError(
      `signPayloadAction: tableId must be 16 bytes, got ${tableId.length}`,
    );
  }
  const actionBytes = encAction(action);
  const payload = concatBytes(
    DOMAIN_ACTION,
    tableId,
    u64LE(handSeq),
    u64LE(actionSeq),
    actionBytes,
  );
  return blake3(payload, { dkLen: 32 });
}

export interface ActionEnvelopeFields {
  tableId: Uint8Array; // 16 bytes
  handSeq: bigint;
  actionSeq: bigint;
  playerId: Uint8Array; // 32 bytes
  action: ActionView;
  sig: Uint8Array; // 64 bytes
  /** Optional explicit credential (rotated / hybrid). ABSENT => genesis
   *  (the `[0x00, 0x00]` trailing-None shape, byte-identical to today). */
  signingCred?: SigningCredFields | null;
  /** Optional ML-DSA-65 second leg (hybrid AND-mode). */
  mlDsaLeg?: MlDsaLegFields | null;
}

/**
 * Postcard-encode an `ActionEnvelope` for transport. The result is
 * byte-equal to `postcard::to_allocvec(&ActionEnvelope { ... })` on
 * the Rust side. The 8-case cross-impl fixture in
 * `tests/cross_impl/envelope_8case.test.ts` locks in the None-path parity;
 * `tests/cross_impl/envelope_some_case.test.ts` locks in the Some path.
 *
 * Postcard is positional + non-self-describing, so EVERY envelope must
 * emit BOTH trailing positional `Option` discriminants after `sig`:
 *   - Non-hybrid/genesis: `[0x00, 0x00]` (UNCHANGED — byte-identical to
 *     today + the 8-case fixture).
 *   - Rotated ed25519-only: `encSigningCredOption(Some) || 0x00`.
 *   - Hybrid: `encSigningCredOption(Some ed25519 cred) ||
 *     encMlDsaLegOption(Some {epoch, sig})`.
 */
export function encActionEnvelope(env: ActionEnvelopeFields): Uint8Array {
  if (env.tableId.length !== 16) {
    throw new RangeError("encActionEnvelope: tableId 16B");
  }
  if (env.playerId.length !== 32) {
    throw new RangeError("encActionEnvelope: playerId 32B");
  }
  if (env.sig.length !== 64) {
    throw new RangeError("encActionEnvelope: sig 64B");
  }
  return concatBytes(
    env.tableId,
    vu64(env.handSeq),
    vu64(env.actionSeq),
    env.playerId,
    encAction(env.action),
    env.sig,
    // Trailing positional postcard `Option`s: `signing_cred` then
    // `ml_dsa_leg`. Both discriminants are required for byte-equality
    // with the Rust encoder — `null` emits the `0x00` None byte.
    encSigningCredOption(env.signingCred ?? null),
    encMlDsaLegOption(env.mlDsaLeg ?? null),
  );
}

/**
 * Convenience builder — sign + encode in one shot. The caller
 * supplies the 32-byte ed25519 signing key (seed); the public key
 * is derived from the same seed so it always matches the broadcast
 * `player_id`. Returns the byte sequence the route layer expects
 * as the hex body of `POST /tables/:id/action`.
 *
 * **Signing-key lifetime** — the caller passes the SAME
 * `signingKey` for every action in a session. Per-call `wipeSeed`
 * would force the caller to re-derive via Argon2id on every action
 * (a ~500-1000 ms cost) which is operationally untenable. The
 * correct wipe site is session-end / logout (or page unload) — the
 * caller (login flow) owns that lifecycle. Do NOT wipe inside this
 * helper.
 */
export function signAndEncodeAction(args: {
  tableId: Uint8Array;
  handSeq: bigint;
  actionSeq: bigint;
  signingKey: Uint8Array; // 32-byte ed25519 seed
  action: ActionView;
  /** Explicit ed25519 credential epoch when rotated above genesis. When
   *  `undefined` / `0` on a genesis account the envelope stays the
   *  byte-identical `[0x00, 0x00]` None shape (no signing_cred emitted). */
  ed25519Epoch?: number;
  /** Hybrid second leg: the 32-byte ML-DSA-65 SEED. When present the
   *  envelope carries BOTH legs (signing_cred Some + ml_dsa_leg Some). */
  mlDsaSeed?: Uint8Array | null;
  /** The ml-dsa credential epoch hint (DB-current). */
  mlDsaEpoch?: number | null;
  /** When `true`, the ml-dsa leg is signed deterministically
   *  (`extraEntropy:false`) for byte-reproducibility against the Rust
   *  fixture; the live signer omits this and gets hedged signing. */
  mlDsaDeterministic?: boolean;
}): {
  envelope: Uint8Array;
  envelopeHex: string;
  playerId: Uint8Array;
  sig: Uint8Array;
} {
  if (args.signingKey.length !== 32) {
    throw new RangeError("signAndEncodeAction: signingKey 32B");
  }
  const payload = signPayloadAction(
    args.tableId,
    args.handSeq,
    args.actionSeq,
    args.action,
  );
  const sig = ed25519.sign(payload, args.signingKey);
  const playerId = ed25519.getPublicKey(args.signingKey);

  const hybrid = args.mlDsaSeed != null;
  // The signing_cred is emitted when the ed25519 leg is rotated above
  // genesis (epoch > 0) OR a hybrid leg is present (the BE keys the
  // epoch-trailer off this cred; a hybrid genesis ed25519 leg still emits
  // signing_cred with epoch 0 so the choke reconciles both legs).
  const edEpoch = args.ed25519Epoch ?? 0;
  const needsSigningCred = edEpoch > 0 || hybrid;
  const signingCred: SigningCredFields | null = needsSigningCred
    ? { epoch: edEpoch, kind: "ed25519", pubkey: playerId }
    : null;

  let mlDsaLeg: MlDsaLegFields | null = null;
  if (hybrid) {
    // The ml-dsa leaf is preloaded at boot when a hybrid seed is restored
    // (see `preloadMlDsa` / `main.tsx`); this sync path reads the cached
    // module so `signAndEncodeAction` stays synchronous.
    const mlSig = getCachedDeriveMlDsa()(args.mlDsaSeed!).sign(payload, {
      hedged: args.mlDsaDeterministic === true ? false : true,
    });
    mlDsaLeg = { epoch: args.mlDsaEpoch ?? 0, sig: mlSig };
  }

  const envelope = encActionEnvelope({
    tableId: args.tableId,
    handSeq: args.handSeq,
    actionSeq: args.actionSeq,
    playerId,
    action: args.action,
    sig,
    signingCred,
    mlDsaLeg,
  });
  return { envelope, envelopeHex: bytesToHex(envelope), playerId, sig };
}
